import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Recharts' built-in <Tooltip> picks the active bar from mouse page coordinates minus the
// chart's getBoundingClientRect(). Under CSS `zoom` (report pages, see uiScale) those two
// disagree, so it shows the wrong bar / wrong place. This tooltip is driven by the bar's
// own hover events and positioned with the bar's chart coordinates, so zoom never matters.
export const useBarHoverTooltip = () => {
  const [hoveredBar, setHoveredBar] = useState(null);

  // Recharts calls Bar event handlers with the rectangle props (x, y, width, value, payload).
  const onMouseEnter = useCallback(bar => {
    setHoveredBar({
      x: bar.x + bar.width / 2,
      y: bar.y,
      value: bar.value,
      payload: bar.payload,
    });
  }, []);
  const onMouseLeave = useCallback(() => setHoveredBar(null), []);

  const barHoverHandlers = useMemo(() => ({ onMouseEnter, onMouseLeave }), [
    onMouseEnter,
    onMouseLeave,
  ]);

  return { hoveredBar, barHoverHandlers };
};

// Render inside a `position: relative` box that wraps the <ResponsiveContainer>.
export const BarHoverTooltip = ({ hoveredBar, children }) => {
  if (!hoveredBar) return null;
  return (
    <div
      style={{
        position: 'absolute',
        left: hoveredBar.x,
        top: hoveredBar.y,
        transform: 'translate(-50%, calc(-100% - 8px))',
        pointerEvents: 'none',
        zIndex: 2,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
};

BarHoverTooltip.propTypes = {
  hoveredBar: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  children: PropTypes.node,
};
